import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, EmptyState, Field, Icon, Input, Select, Spinner, StatusPill, useToast } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { addDaysIso, datePt, dayMonth, statusLabel, todayIso, weekdayLong, weekdayOfIso, weekdayShort } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

interface AgendaAppt {
  id: string;
  scheduled_date: string;
  start_time: string;
  end_time: string;
  service_name: string;
  status: string;
  customerName: string;
  professionalName: string;
}

interface ProfessionalLite {
  id: string;
  name: string;
}

interface ServiceLite {
  id: string;
  name: string;
  status: string;
}

interface HoursEntryState {
  active: boolean;
  opensAt: string;
  closesAt: string;
  breakStart: string;
  breakEnd: string;
}

interface HoursConfig {
  weekday: number;
  opensAt: string;
  closesAt: string;
  breakStart: string | null;
  breakEnd: string | null;
}

interface ExceptionRow {
  id: string;
  type: 'day_off' | 'holiday' | 'vacation' | 'extra_day' | 'blocked';
  start_date: string;
  end_date: string;
  start_time: string | null;
  end_time: string | null;
  reason: string | null;
  professional_id: string | null;
  professionalName: string | null;
}

interface Slot {
  start: string;
  end: string;
}

const TONE: Record<string, 'neutral' | 'positive' | 'warning' | 'danger'> = {
  pending: 'warning',
  confirmed: 'positive',
  no_show: 'danger'
};

const EXCEPTION_LABEL: Record<string, string> = {
  day_off: 'Folga',
  holiday: 'Feriado',
  vacation: 'Férias',
  extra_day: 'Dia extra',
  blocked: 'Bloqueio de horário'
};

type ViewKind = 'day' | 'week' | 'month' | 'list';
type Tab = 'agenda' | 'hours' | 'exceptions' | 'availability';

const DEFAULT_DAY_STATE: HoursEntryState = { active: false, opensAt: '09:00', closesAt: '18:00', breakStart: '12:00', breakEnd: '13:00' };

export function PanelSchedulePage(): JSX.Element {
  usePageMeta(tr('Agenda'), tr('Visões diária, semanal e mensal da agenda, expediente, exceções e horários livres calculados pelo servidor.'));
  const { active } = useAuth();
  const toast = useToast();
  const businessId = active?.businessId ?? '';
  const canEditHours = active ? active.role === 'owner' || active.role === 'manager' : false;
  const centralAgenda = active ? active.role !== 'professional' || active.permissions.includes('central_agenda') : false;

  const [tab, setTab] = useState<Tab>('agenda');
  const [view, setView] = useState<ViewKind>('day');
  const [cursor, setCursor] = useState(todayIso());
  const [professionalFilter, setProfessionalFilter] = useState('');
  const [professionals, setProfessionals] = useState<ProfessionalLite[]>([]);
  const [services, setServices] = useState<ServiceLite[]>([]);

  const [agenda, setAgenda] = useState<AgendaAppt[]>([]);
  const [agendaLoading, setAgendaLoading] = useState(true);
  const [agendaError, setAgendaError] = useState<unknown>(null);

  const [hours, setHours] = useState<HoursEntryState[]>([]);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [hoursError, setHoursError] = useState<unknown>(null);
  const [savingHours, setSavingHours] = useState(false);
  const [proHours, setProHours] = useState<HoursEntryState[]>([]);
  const [savingProHours, setSavingProHours] = useState(false);

  const [exceptions, setExceptions] = useState<ExceptionRow[]>([]);
  const [excLoading, setExcLoading] = useState(false);
  const [excError, setExcError] = useState<unknown>(null);
  const [excBusy, setExcBusy] = useState<string | null>(null);

  const [availProf, setAvailProf] = useState('');
  const [availService, setAvailService] = useState('');
  const [availDate, setAvailDate] = useState(todayIso());
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [availError, setAvailError] = useState<unknown>(null);

  const range = useMemo(() => computeRange(view, cursor), [view, cursor]);

  useEffect(() => {
    if (!businessId) return;
    void (async () => {
      try {
        const [team, svcs] = await Promise.all([
          api.get<{ team: ProfessionalLite[] }>(`/api/team/${businessId}/team`),
          api.get<{ services: ServiceLite[] }>(`/api/services/${businessId}/services`)
        ]);
        setProfessionals(team.team);
        setServices(svcs.services.filter((s) => s.status === 'active'));
        if (team.team[0]) {
          setAvailProf((v) => v || team.team[0].id);
        }
      } catch (err) {
        toast(errorMessage(err), 'danger');
      }
    })();
  }, [businessId]);

  const loadAgenda = useCallback(async () => {
    if (!businessId) return;
    setAgendaLoading(true);
    setAgendaError(null);
    const params = new URLSearchParams({ from: range.from, to: range.to });
    if (professionalFilter) params.set('professionalId', professionalFilter);
    try {
      const res = await api.get<{ appointments: AgendaAppt[] }>(`/api/schedule/${businessId}/agenda?${params.toString()}`);
      setAgenda(res.appointments);
    } catch (err) {
      setAgenda([]);
      setAgendaError(err);
    } finally {
      setAgendaLoading(false);
    }
  }, [businessId, range.from, range.to, professionalFilter]);

  useEffect(() => {
    if (tab === 'agenda') void loadAgenda();
  }, [tab, loadAgenda]);

  const loadHours = useCallback(async () => {
    if (!businessId) return;
    setHoursLoading(true);
    setHoursError(null);
    try {
      const res = await api.get<{ businessHours: HoursConfig[]; professionalHours: HoursConfig[] | null }>(`/api/schedule/${businessId}/hours`);
      setHours(daysFromConfig(res.businessHours));
      setProHours(res.professionalHours ? daysFromConfig(res.professionalHours) : []);
    } catch (err) {
      setHoursError(err);
    } finally {
      setHoursLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    if (tab === 'hours') void loadHours();
  }, [tab, loadHours]);

  const loadExceptions = useCallback(async () => {
    if (!businessId) return;
    setExcLoading(true);
    setExcError(null);
    try {
      const res = await api.get<{ exceptions: ExceptionRow[] }>(`/api/schedule/${businessId}/exceptions?from=${todayIso()}`);
      setExceptions(res.exceptions);
    } catch (err) {
      setExceptions([]);
      setExcError(err);
    } finally {
      setExcLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    if (tab === 'exceptions') void loadExceptions();
  }, [tab, loadExceptions]);

  const loadAvailability = useCallback(async () => {
    if (!businessId || !availProf || !availService) return;
    setAvailLoading(true);
    setAvailError(null);
    setSlots(null);
    try {
      const res = await api.get<{ slots: Slot[] }>(
        `/api/schedule/${businessId}/availability?professionalId=${availProf}&serviceId=${availService}&date=${availDate}`
      );
      setSlots(res.slots);
    } catch (err) {
      setAvailError(err);
    } finally {
      setAvailLoading(false);
    }
  }, [businessId, availProf, availService, availDate]);

  useEffect(() => {
    if (tab === 'availability') void loadAvailability();
  }, [tab, loadAvailability]);

  const saveHours = async (): Promise<void> => {
    setSavingHours(true);
    try {
      const entries = hours
        .map((h, weekday) => ({ weekday, ...h }))
        .filter((h) => h.active)
        .map((h) => ({
          weekday: h.weekday,
          opensAt: h.opensAt,
          closesAt: h.closesAt,
          breakStart: h.breakStart || null,
          breakEnd: h.breakEnd || null
        }));
      await api.put(`/api/schedule/${businessId}/hours`, { entries });
      toast(tr('Expediente do estabelecimento salvo.'), 'success');
      await loadHours();
    } catch (err) {
      toast(errorMessage(err), 'danger');
    } finally {
      setSavingHours(false);
    }
  };

  const saveProfessionalHours = async (): Promise<void> => {
    if (!active?.professionalId || proHours.length === 0) return;
    setSavingProHours(true);
    try {
      const entries = proHours
        .map((h, weekday) => ({ weekday, ...h }))
        .filter((h) => h.active)
        .map((h) => ({
          weekday: h.weekday,
          opensAt: h.opensAt,
          closesAt: h.closesAt,
          breakStart: h.breakStart || null,
          breakEnd: h.breakEnd || null
        }));
      await api.put(`/api/schedule/${businessId}/hours/${active.professionalId}`, { entries });
      toast(tr('Seu horário de trabalho foi salvo.'), 'success');
      await loadHours();
    } catch (err) {
      toast(errorMessage(err), 'danger');
    } finally {
      setSavingProHours(false);
    }
  };

  const deleteException = async (id: string): Promise<void> => {
    setExcBusy(id);
    try {
      await api.del(`/api/schedule/${businessId}/exceptions/${id}`);
      toast(tr('Exceção removida da agenda.'), 'success');
      await loadExceptions();
    } catch (err) {
      toast(errorMessage(err), 'danger');
    } finally {
      setExcBusy(null);
    }
  };

  const periodLabel = (): string => {
    if (view === 'day') return datePt(cursor);
    if (view === 'week') return `${datePt(range.from)} – ${datePt(range.to)}`;
    const [y, m] = cursor.split('-');
    const name = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const shift = (delta: number): void => {
    if (view === 'day') setCursor(addDaysIso(cursor, delta));
    else if (view === 'week') setCursor(addDaysIso(cursor, delta * 7));
    else {
      const [y, m] = cursor.split('-').map(Number);
      const d = new Date(y ?? 2026, (m ?? 1) - 1 + delta, 1);
      setCursor(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
    }
  };

  const byDay = useMemo(() => {
    const map = new Map<string, AgendaAppt[]>();
    for (const a of agenda) {
      const list = map.get(a.scheduled_date) ?? [];
      list.push(a);
      map.set(a.scheduled_date, list);
    }
    return map;
  }, [agenda]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{tr("Agenda")}</h1>
        <p className="nl-hint">{tr("Compromissos, expediente, folgas e horários livres — os intervalos disponíveis são calculados pelo servidor.")}</p>
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label={tr("Seções da agenda")}>
        {(
          [
            ['agenda', 'Compromissos'],
            ['hours', 'Expediente'],
            ['exceptions', 'Folgas e exceções'],
            ['availability', 'Horários livres']
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${tab === key ? 'bg-accent text-on-accent shadow-[0_8px_16px_-10px_var(--nl-accent)]' : 'border border-line bg-card hover:bg-surface'}`}
          >
            {tr(label)}
          </button>
        ))}
      </div>

      {tab === 'agenda' ? (
        <Card as="section" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
              <Button variant="ghost" onClick={() => shift(-1)} aria-label={tr("Período anterior")} icon={<Icon name="back" size={16} />} disabled={agendaLoading}>
                {tr("Anterior")}
              </Button>
              <Button variant="secondary" onClick={() => setCursor(todayIso())} disabled={agendaLoading}>
                {tr("Hoje")}
              </Button>
              <Button variant="ghost" onClick={() => shift(1)} disabled={agendaLoading}>
                {tr("Próximo")} <Icon name="chevron" size={16} />
              </Button>
            </div>
            <p className="font-semibold">{periodLabel()}</p>
            <Select className="max-w-40" value={view} onChange={(e) => setView(e.target.value as ViewKind)} aria-label={tr("Modo de visão")}>
              <option value="day">{tr("Diária")}</option>
              <option value="week">{tr("Semanal")}</option>
              <option value="month">{tr("Mensal")}</option>
              <option value="list">{tr("Lista")}</option>
            </Select>
            {centralAgenda ? (
              <Select
                className="max-w-52"
                value={professionalFilter}
                onChange={(e) => setProfessionalFilter(e.target.value)}
                aria-label={tr("Filtrar por profissional")}
              >
                <option value="">{tr("Todos os profissionais")}</option>
                {professionals.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            ) : (
              <span className="nl-hint text-sm">{tr("Sua agenda individual (a central exige permissão).")}</span>
            )}
          </div>

          {agendaLoading ? <Spinner label={tr("Carregando compromissos…")} /> : null}
          {!agendaLoading && agendaError ? <ApiAlert error={agendaError} /> : null}

          {!agendaLoading && !agendaError ? (
            agenda.length === 0 ? (
              <EmptyState title={tr("Nenhum compromisso no período")} description={tr("Pendentes, confirmados e faltas aparecem aqui. Cancelados ficam na tela de Agendamentos.")} />
            ) : view === 'day' ? (
              <ul className="divide-y divide-line">
                {(byDay.get(cursor) ?? []).map((a) => (
                  <ApptItem key={a.id} a={a} />
                ))}
                {(byDay.get(cursor) ?? []).length === 0 ? <li className="py-4 text-sm text-muted">{tr("Nada agendado em")} {datePt(cursor)}.</li> : null}
              </ul>
            ) : view === 'week' ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
                {weekDays(cursor).map((day) => (
                  <div key={day} className="rounded-2xl border border-line p-2">
                    <p className="mb-1 text-xs font-semibold text-muted">
                      {weekdayShort(weekdayOfIso(day))} {dayMonth(day)}
                    </p>
                    {(byDay.get(day) ?? []).map((a) => (
                      <p key={a.id} className="mb-1 truncate text-xs" title={`${a.start_time} ${a.service_name} — ${a.customerName}`}>
                        {a.start_time} {a.customerName}
                      </p>
                    ))}
                    {!byDay.get(day)?.length ? <p className="text-xs text-muted">—</p> : null}
                  </div>
                ))}
              </div>
            ) : view === 'month' ? (
              <MonthGrid cursor={cursor} byDay={byDay} />
            ) : (
              <ul className="divide-y divide-line">
                {agenda.map((a) => (
                  <ApptItem key={a.id} a={a} showDate />
                ))}
              </ul>
            )
          ) : null}
        </Card>
      ) : null}

      {tab === 'hours' ? (
        <Card as="section" className="space-y-4">
          <h2 className="text-lg font-semibold">{tr("Expediente do estabelecimento")}</h2>
          {hoursLoading ? <Spinner label={tr("Carregando expediente…")} /> : null}
          {!hoursLoading && hoursError ? <ApiAlert error={hoursError} /> : null}
          {!hoursLoading && !hoursError ? (
            <>
              {hours.length === 0 ? <p className="nl-hint">{tr("Nenhum dia configurado — marque os dias em que o estabelecimento atende.")}</p> : null}
              <div className="space-y-2">
                {hours.map((h, weekday) => (
                  <div key={weekday} className="flex flex-wrap items-end gap-2 rounded-2xl border border-line p-3">
                    <label className="flex items-center gap-2 text-sm font-semibold sm:w-32">
                      <input
                        type="checkbox"
                        checked={h.active}
                        disabled={!canEditHours}
                        onChange={(e) => setHours((prev) => prev.map((p, i) => (i === weekday ? { ...p, active: e.target.checked } : p)))}
                      />
                      {weekdayLong(weekday)}
                    </label>
                    <Field label={tr("Abertura")}>
                      {({ id }) => (
                        <Input id={id} type="time" className="w-32" value={h.opensAt} disabled={!canEditHours || !h.active}
                          onChange={(e) => setHours((prev) => prev.map((p, i) => (i === weekday ? { ...p, opensAt: e.target.value } : p)))} />
                      )}
                    </Field>
                    <Field label={tr("Fechamento")}>
                      {({ id }) => (
                        <Input id={id} type="time" className="w-32" value={h.closesAt} disabled={!canEditHours || !h.active}
                          onChange={(e) => setHours((prev) => prev.map((p, i) => (i === weekday ? { ...p, closesAt: e.target.value } : p)))} />
                      )}
                    </Field>
                    <Field label={tr("Início do almoço")}>
                      {({ id }) => (
                        <Input id={id} type="time" className="w-32" value={h.breakStart} disabled={!canEditHours || !h.active}
                          onChange={(e) => setHours((prev) => prev.map((p, i) => (i === weekday ? { ...p, breakStart: e.target.value } : p)))} />
                      )}
                    </Field>
                    <Field label={tr("Fim do almoço")}>
                      {({ id }) => (
                        <Input id={id} type="time" className="w-32" value={h.breakEnd} disabled={!canEditHours || !h.active}
                          onChange={(e) => setHours((prev) => prev.map((p, i) => (i === weekday ? { ...p, breakEnd: e.target.value } : p)))} />
                      )}
                    </Field>
                  </div>
                ))}
              </div>
              {!canEditHours ? (
                <Alert tone="info" title={tr("Somente leitura")}>{tr("Apenas proprietário e gerente alteram o expediente do estabelecimento.")}</Alert>
              ) : (
                <Button onClick={() => void saveHours()} loading={savingHours} disabled={savingHours}>
                  {tr("Salvar expediente")}
                </Button>
              )}
            </>
          ) : null}

          {active?.professionalId ? (
            <div className="border-t border-line pt-4">
              <h2 className="mb-2 text-lg font-semibold">{tr("Meu horário de profissional")}</h2>
              <p className="nl-hint mb-3">{tr("Quando definido, seu horário prevalece sobre o expediente geral nos dias correspondentes.")}</p>
              {proHours.length === 0 ? (
                <p className="text-sm text-muted">{tr("Sem horário próprio salvo — vale o expediente do estabelecimento.")}</p>
              ) : (
                <>
                  <div className="space-y-2">
                    {proHours.map((h, weekday) => (
                      <div key={weekday} className="flex flex-wrap items-end gap-2 rounded-2xl border border-line p-3">
                        <label className="flex items-center gap-2 text-sm font-semibold sm:w-32">
                          <input type="checkbox" checked={h.active} onChange={(e) => setProHours((prev) => prev.map((p, i) => (i === weekday ? { ...p, active: e.target.checked } : p)))} />
                          {weekdayLong(weekday)}
                        </label>
                        <Input type="time" className="w-32" value={h.opensAt} disabled={!h.active} onChange={(e) => setProHours((prev) => prev.map((p, i) => (i === weekday ? { ...p, opensAt: e.target.value } : p)))} aria-label={tr("Abertura")} />
                        <Input type="time" className="w-32" value={h.closesAt} disabled={!h.active} onChange={(e) => setProHours((prev) => prev.map((p, i) => (i === weekday ? { ...p, closesAt: e.target.value } : p)))} aria-label={tr("Fechamento")} />
                        <Input type="time" className="w-32" value={h.breakStart} disabled={!h.active} onChange={(e) => setProHours((prev) => prev.map((p, i) => (i === weekday ? { ...p, breakStart: e.target.value } : p)))} aria-label={tr("Início do intervalo")} />
                        <Input type="time" className="w-32" value={h.breakEnd} disabled={!h.active} onChange={(e) => setProHours((prev) => prev.map((p, i) => (i === weekday ? { ...p, breakEnd: e.target.value } : p)))} aria-label={tr("Fim do intervalo")} />
                      </div>
                    ))}
                  </div>
                  <Button className="mt-3" onClick={() => void saveProfessionalHours()} loading={savingProHours} disabled={savingProHours}>
                    {tr("Salvar meu horário")}
                  </Button>
                </>
              )}
            </div>
          ) : null}
        </Card>
      ) : null}

      {tab === 'exceptions' ? (
        <div className="grid gap-5 lg:grid-cols-2">
          <Card as="section">
            <h2 className="mb-3 text-lg font-semibold">{tr("Exceções futuras (a partir de hoje)")}</h2>
            {excLoading ? <Spinner label={tr("Carregando exceções…")} /> : null}
            {!excLoading && excError ? <ApiAlert error={excError} /> : null}
            {!excLoading && !excError && exceptions.length === 0 ? (
              <EmptyState title={tr("Nenhuma folga, feriado, férias, dia extra ou bloqueio registrado")} />
            ) : null}
            <ul className="divide-y divide-line">
              {exceptions.map((e) => (
                <li key={e.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="font-semibold">
                      {tr(EXCEPTION_LABEL[e.type] ?? e.type)} · {datePt(e.start_date)}
                      {e.end_date !== e.start_date ? ` a ${datePt(e.end_date)}` : ''}
                    </p>
                    <p className="nl-hint truncate">
                      {e.start_time && e.end_time ? `${e.start_time}–${e.end_time} · ` : ''}
                      {e.professionalName ? `Profissional: ${e.professionalName}` : 'Todo o estabelecimento'}
                      {e.reason ? ` · ${e.reason}` : ''}
                    </p>
                  </div>
                  <Button variant="danger" loading={excBusy === e.id} disabled={excBusy !== null} onClick={() => void deleteException(e.id)}>
                    {tr("Remover")}
                  </Button>
                </li>
              ))}
            </ul>
          </Card>
          <ExceptionForm
            businessId={businessId}
            professionals={professionals}
            showProfessionalSelect={centralAgenda}
            onCreated={async () => {
              toast(tr('Exceção registrada na agenda.'), 'success');
              await loadExceptions();
            }}
          />
        </div>
      ) : null}

      {tab === 'availability' ? (
        <Card as="section" className="space-y-4">
          <h2 className="text-lg font-semibold">{tr("Horários livres")}</h2>
          <p className="nl-hint">{tr("Calculados pelo servidor considerando expediente, seu horário, folgas, bloqueios e ocupação — nada é estimado aqui.")}</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={tr("Profissional")} required>
              {({ id }) => (
                <Select id={id} value={availProf} onChange={(e) => setAvailProf(e.target.value)}>
                  <option value="">{tr("Selecione…")}</option>
                  {professionals.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={tr("Serviço")} required>
              {({ id }) => (
                <Select id={id} value={availService} onChange={(e) => setAvailService(e.target.value)}>
                  <option value="">{tr("Selecione…")}</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={tr("Data")}>
              {({ id }) => <Input id={id} type="date" min={todayIso()} value={availDate} onChange={(e) => setAvailDate(e.target.value)} />}
            </Field>
          </div>
          {!availProf || !availService ? (
            <EmptyState title={tr("Escolha profissional e serviço")} description={tr("A duração e as regras do serviço definem o tamanho das janelas livres.")} />
          ) : availLoading ? (
            <Spinner label={tr("Calculando disponibilidade…")} />
          ) : availError ? (
            <ApiAlert error={availError} />
          ) : slots && slots.length === 0 ? (
            <EmptyState title={tr("Sem horários livres nesta data")} description={tr("Dia fechado, cheio, fora do horizonte permitido ou com bloqueios.")} />
          ) : (
            <div className="flex flex-wrap gap-2">
              {(slots ?? []).map((s) => (
                <span key={`${s.start}-${s.end}`} className="rounded-full border border-sage bg-positive-soft px-4 py-1.5 text-sm font-semibold text-graphite">
                  {s.start} – {s.end}
                </span>
              ))}
            </div>
          )}
        </Card>
      ) : null}
    </div>
  );
}

function ApptItem({ a, showDate }: { a: AgendaAppt; showDate?: boolean }): JSX.Element {
  return (
    <li className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <p className="font-semibold">
          {showDate ? `${datePt(a.scheduled_date)} ` : ''}
          {a.start_time}–{a.end_time} · {a.service_name}
        </p>
        <p className="nl-hint truncate">
          {a.customerName} — {a.professionalName}
        </p>
      </div>
      <StatusPill tone={TONE[a.status] ?? 'neutral'}>{statusLabel(a.status)}</StatusPill>
    </li>
  );
}

function MonthGrid({ cursor, byDay }: { cursor: string; byDay: Map<string, AgendaAppt[]> }): JSX.Element {
  const [y, m] = cursor.split('-').map(Number);
  const daysInMonth = new Date(y ?? 2026, m ?? 1, 0).getDate();
  const cells: string[] = [];
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
      {cells.map((day) => {
        const list = byDay.get(day) ?? [];
        return (
          <div key={day} className={`min-h-20 rounded-2xl border p-2 text-xs ${day === todayIso() ? 'border-sage bg-positive-soft' : 'border-line'}`}>
            <p className="font-semibold">{Number(day.slice(8, 10))}</p>
            {list.slice(0, 3).map((a) => (
              <p key={a.id} className="truncate" title={`${a.start_time} ${a.service_name} — ${a.customerName}`}>
                {a.start_time} {a.customerName}
              </p>
            ))}
            {list.length > 3 ? <p className="text-muted">+{list.length - 3} {tr("mais")}</p> : null}
            {list.length === 0 ? <p className="text-muted">—</p> : null}
          </div>
        );
      })}
    </div>
  );
}

function ExceptionForm({
  businessId,
  professionals,
  showProfessionalSelect,
  onCreated
}: {
  businessId: string;
  professionals: ProfessionalLite[];
  showProfessionalSelect: boolean;
  onCreated: () => Promise<void>;
}): JSX.Element {
  const [type, setType] = useState<ExceptionRow['type']>('day_off');
  const [professionalId, setProfessionalId] = useState('');
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const needsTimes = type === 'blocked';
  const allowsTimes = type === 'blocked' || type === 'extra_day';

  const submit = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/schedule/${businessId}/exceptions`, {
        type,
        professionalId: professionalId || null,
        startDate,
        endDate,
        startTime: allowsTimes && startTime ? startTime : null,
        endTime: allowsTimes && endTime ? endTime : null,
        reason: reason.trim() || undefined
      });
      setReason('');
      setStartTime('');
      setEndTime('');
      await onCreated();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const valid = /^\d{4}-\d{2}-\d{2}$/.test(startDate) && /^\d{4}-\d{2}-\d{2}$/.test(endDate) && endDate >= startDate && (!needsTimes || (!!startTime && !!endTime));

  return (
    <Card as="section" className="space-y-3">
      <h2 className="text-lg font-semibold">{tr("Registrar exceção")}</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={tr("Tipo")} required>
          {({ id }) => (
            <Select id={id} value={type} onChange={(e) => setType(e.target.value as ExceptionRow['type'])}>
              <option value="day_off">{tr("Folga (dia inteiro)")}</option>
              <option value="holiday">{tr("Feriado (dia inteiro)")}</option>
              <option value="vacation">{tr("Férias")}</option>
              <option value="extra_day">{tr("Dia extra (fora do expediente)")}</option>
              <option value="blocked">{tr("Bloqueio de horário")}</option>
            </Select>
          )}
        </Field>
        {showProfessionalSelect ? (
          <Field label={tr("Profissional")} hint="Vazio = exceção para todo o estabelecimento.">
            {({ id }) => (
              <Select id={id} value={professionalId} onChange={(e) => setProfessionalId(e.target.value)}>
                <option value="">{tr("Todos")}</option>
                {professionals.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}
        <Field label={tr("Data inicial")} required>
          {({ id }) => <Input id={id} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />}
        </Field>
        <Field label={tr("Data final")} required>
          {({ id }) => <Input id={id} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />}
        </Field>
        {allowsTimes ? (
          <>
            <Field label={needsTimes ? tr('Horário inicial') : tr('Horário inicial (opcional)')} required={needsTimes}>
              {({ id }) => <Input id={id} type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />}
            </Field>
            <Field label={needsTimes ? tr('Horário final') : tr('Horário final (opcional)')} required={needsTimes}>
              {({ id }) => <Input id={id} type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />}
            </Field>
          </>
        ) : (
          <p className="nl-hint text-sm sm:col-span-2">{tr("Para folgas, feriados e férias informe apenas as datas (dia inteiro).")}</p>
        )}
      </div>
      <Field label={tr("Motivo (opcional)")}>
        {({ id }) => <Input id={id} maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
      {error ? <ApiAlert error={error} /> : null}
      <Button onClick={() => void submit()} loading={saving} disabled={saving || !valid}>
        {tr("Registrar")}
      </Button>
    </Card>
  );
}

function computeRange(view: ViewKind, cursor: string): { from: string; to: string } {
  if (view === 'day') return { from: cursor, to: cursor };
  if (view === 'week') {
    const start = addDaysIso(cursor, -weekdayOfIso(cursor));
    return { from: start, to: addDaysIso(start, 6) };
  }
  const [y, m] = cursor.split('-').map(Number);
  const last = new Date(y ?? 2026, m ?? 1, 0).getDate();
  const mm = String(m).padStart(2, '0');
  return { from: `${y}-${mm}-01`, to: `${y}-${mm}-${String(last).padStart(2, '0')}` };
}

function weekDays(cursor: string): string[] {
  const start = addDaysIso(cursor, -weekdayOfIso(cursor));
  return Array.from({ length: 7 }, (_, i) => addDaysIso(start, i));
}

function daysFromConfig(config: HoursConfig[]): HoursEntryState[] {
  return Array.from({ length: 7 }, (_, weekday) => {
    const found = config.find((c) => c.weekday === weekday);
    return found
      ? { active: true, opensAt: found.opensAt, closesAt: found.closesAt, breakStart: found.breakStart ?? '', breakEnd: found.breakEnd ?? '' }
      : { ...DEFAULT_DAY_STATE };
  });
}

function ApiAlert({ error }: { error: unknown }): JSX.Element {
  if (error instanceof ApiError && error.status === 402) {
    return (
      <Alert tone="danger" title={tr("Assinatura necessária")}>
        {error.message}{' '}
        <Link className="font-semibold underline" to="/painel/plano">
          {tr("Ir para Meu plano")}
        </Link>
        .
      </Alert>
    );
  }
  if (error instanceof ApiError && error.status === 403) {
    return (
      <Alert tone="warning" title={tr("Permissão insuficiente")}>
        {tr("Seu perfil não pode executar esta ação na agenda.")} {error.message}
      </Alert>
    );
  }
  return <Alert tone="danger" title={tr("Erro")}>{error instanceof Error ? error.message : 'Não foi possível concluir a operação.'}</Alert>;
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 402) return tr('{message} Contrate um plano em /painel/plano.', { message: err.message });
  if (err instanceof ApiError && err.status === 403) return tr('Permissão insuficiente. {message}', { message: err.message });
  return err instanceof Error ? err.message : tr('Não foi possível concluir a operação.');
}
